<script setup lang="ts">
import FilterBar from "@/modules/explorer/components/FilterBar.vue";
import DateSelections from "@/modules/explorer/components/DateSelections.vue";
import { useBaseStore } from "@/stores/base";
import { useExplorerStore } from "@/modules/explorer/store";
import BottomNavigation from "@/modules/explorer/components/BottomNavigation.vue";
import { computed, onBeforeMount, onBeforeUnmount, watch, provide } from "vue";
import { debounce } from "vue-debounce";
import dayjs from "dayjs";
import { v4 as uuidv4 } from "uuid";
import { useDocumentHistory } from "@/modules/explorer/documentHistoryStore";
import { useApiClient } from "@/api/client";
import autoConfigureRelatedDimension from "@/modules/explorer/composables/autoConfigureRelatedDimension";
import { useActiveElement } from "@vueuse/core";
import { useExplorerHotkeyBindings } from "@/modules/explorer/composables/ExplorerHotkeyBindings";
import { ActivePin } from "@/modules/explorer/explorerTypes";
import MainBreadcrumbs from "@/ui/components/header/MainBreadcrumbs.vue";
import DocumentDO = App.Data.Explorer.DocumentDO;
import QuickFilterCard from "@/modules/explorer/components/QuickFilter/QuickFilterCard.vue";

provide("fetchDataPrefix", "explorer/");

const activeElement = useActiveElement();

const breadcrumbs = [
	{
		title: "Explorer",
		disabled: true,
		to: "/explorer",
	},
];

const isInputElement = computed(() => {
	return (
		activeElement.value instanceof HTMLInputElement ||
		activeElement.value instanceof HTMLDivElement ||
		activeElement.value instanceof HTMLTextAreaElement
	);
});

watch(isInputElement, (value) => {
	useExplorerStore().explorerState.areHotkeysEnabled = !value;
});

useExplorerHotkeyBindings();

onBeforeMount(() => {
	useBaseStore().appFullWidth = true;
});

onBeforeUnmount(() => {
	useBaseStore().appFullWidth = false;
	useExplorerStore().$reset();
});

const excludeKeys = ["uuid", "parent_uuid"];

function syncHorizontalAndFirstDataTableDimension(value: DocumentDO) {
	for (const dataTable of useExplorerStore().document.data_tables) {
		if (
			dataTable.dimensions.every(
				(dimension) => dimension.id !== value.horizontal_dimension.id
			)
		) {
			dataTable.dimensions[0] =
				value.horizontal_dimension as DocumentDimensionDTO;
		}
	}
}
const documentRequiresDataFetch = (newObj, oldObj, excludeKeys = []) => {
	const newKeys = Object.keys(newObj).filter(
		(key) => !excludeKeys.includes(key)
	);
	const oldKeys = Object.keys(oldObj).filter(
		(key) => !excludeKeys.includes(key)
	);

	// If different number of keys, then something has changed.
	if (newKeys.length !== oldKeys.length) {
		return true;
	}

	for (const key of newKeys) {
		const newVal = newObj[key];
		const oldVal = oldObj[key];

		if (newVal instanceof Date && oldVal instanceof Date) {
			if (newVal.getTime() !== oldVal.getTime()) {
				return true;
			}
		} else if (
			typeof newVal === "object" &&
			newVal !== null &&
			typeof oldVal === "object" &&
			oldVal !== null
		) {
			// Recursive call for nested objects
			if (documentRequiresDataFetch(newVal, oldVal, excludeKeys)) {
				return true;
			}
		} else if (newVal !== oldVal) {
			return true;
		}
	}

	return false;
};

let isUuidRotatedForPendingUpdate = false;

const rotateDocumentUuid = () => {
	const store = useExplorerStore();

	// update UUID's and add to history-chain
	store.document.parent_uuid = store.document.uuid;
	store.document.uuid = uuidv4();
	useDocumentHistory().addDocumentToHistory(store.document);
};

const debouncedSetLastUpdated = debounce(() => {
	const store = useExplorerStore();

	if (
		!store.explorerState.documentFetchedFromHistory &&
		!isUuidRotatedForPendingUpdate
	) {
		rotateDocumentUuid();
	}

	// always reset this documentFetchedFromHistory again:
	isUuidRotatedForPendingUpdate = false;
	store.explorerState.documentFetchedFromHistory = false;
	store.explorerState.lastUpdated = dayjs().toDate();
}, 300);

function setLastUpdated() {
	isUuidRotatedForPendingUpdate = false;
	debouncedSetLastUpdated();
}

const commitViewTabChange = () => {
	if (!useExplorerStore().explorerState.documentFetchedFromHistory) {
		rotateDocumentUuid();
		isUuidRotatedForPendingUpdate = true;
	}

	debouncedSetLastUpdated();
};

watch(
	() => useExplorerStore().explorerState.lastOpenedSavedDocumentDocumentUuid,
	async (value) => {
		// todo known issue when 2 documents are opened directly after each other watcher is not triggered ( set to null before open is completed )
		// todo come up with better way to fix this ( without setTimeout )

		// load the saved document to the explorerState
		if (value) {
			setTimeout(async () => {
				const response = await useApiClient("explorer").getStoredDocument(
					useExplorerStore().explorerState.lastOpenedSavedDocumentDocumentUuid
				);
				if (response.isOk()) {
					useExplorerStore().explorerState.lastOpenedSavedDocument =
						response.value;
				}
			}, 1000);
		}
	},
	{ immediate: true }
);

watch(
	() => ({ ...useExplorerStore().document }),
	(value, oldValue) => {
		// For some reason with reactivity. The dates do not change but always point to same reference.
		// So i created a separate watcher for dates below.

		if (
			value.horizontal_dimension.name_clean !==
			oldValue.horizontal_dimension.name_clean
		) {
			syncHorizontalAndFirstDataTableDimension(value);
		}

		if (documentRequiresDataFetch(value, oldValue, excludeKeys)) {
			const isOnlyViewTabChange =
				value.view_tab !== oldValue.view_tab &&
				!documentRequiresDataFetch(value, oldValue, [
					...excludeKeys,
					"view_tab",
				]);

			if (isOnlyViewTabChange) {
				commitViewTabChange();
			} else {
				setLastUpdated();
			}
		}
	},
	{ deep: true }
);

watch(
	() => ({ ...useExplorerStore().document?.period_selection }),
	() => {
		setLastUpdated();
	},
	{ deep: true }
);

watch(
	() => ({ ...useExplorerStore().document?.active_filters }),
	() => {
		setLastUpdated();
	},
	{ deep: true }
);

watch(
	() => ({ ...useExplorerStore().document?.dimension_defaults }),
	() => {
		setLastUpdated();
	},
	{ deep: true }
);

// Logic for date comparison.
// Auto enables date_comparison dimension when the comparison is started.
// Disables date_comparison dimension when the comparison has no years.
autoConfigureRelatedDimension(setLastUpdated);

watch(
	() => ({ ...useExplorerStore().document?.date_comparison }),
	() => {
		setLastUpdated();
	},
	{ deep: true }
);

watch(
	() => [...useExplorerStore().document.data_tables],
	() => setLastUpdated(),
	{ deep: true }
);

const visiblePins = computed(() => {
	return useExplorerStore().explorerState.activePins.filter(
		(activePin: ActivePin) =>
			!useExplorerStore().ACTIVE_DIMENSIONS.includes(
				activePin.dimension.name_clean
			)
	);
});

const activeFilters = computed(() => {
	return useExplorerStore().document.active_filters;
});

const openQuickFilters = computed(() =>
	useExplorerStore().document.active_filters.filter(
		(active_filter) => active_filter.isQuickFilter && active_filter.isApplied
	)
);
</script>

<template>
	<main-breadcrumbs :breadcrumbs="breadcrumbs" />

	<div class="h-100 d-flex flex-column ga-3" style="z-index: 1">
		<v-card class="px-2 py-0">
			<v-expand-transition>
				<div v-if="!useExplorerStore().explorerState.showDataFullScreen">
					<date-selections />

					<v-divider />

					<filter-bar
						surface="explorer"
						:active-filters="activeFilters"
						:visible-pins="visiblePins"
						style="min-height: 40px"
					/>
				</div>
			</v-expand-transition>
		</v-card>

		<QuickFilterCard :quick-filters="openQuickFilters" is-explorer />

		<div class="flex-grow-1">
			<router-view />
		</div>
	</div>

	<bottom-navigation />

	<teleport to="#secondary-drawer">
		<component
			:is="useBaseStore().secondaryDrawerComponent"
			v-if="useBaseStore().showSecondaryDrawer"
		/>
	</teleport>
</template>
